/**
 * Tabellino (V24), la scheda Tabellino della partita conclusa: chi è stato in
 * campo, con i minuti e gli eventi di ciascuno, in una tabella ordinabile.
 * Nella cella del nome i segnali della partita del giocatore, in ordine di
 * minuto: entrata e uscita con la freccia, giallo e rosso con il cartellino.
 *
 * Tutto arriva calcolato dagli eventi non cancellati: nulla è memorizzato e
 * nulla si modifica da qui. Le correzioni passano da Modifica (V25). Qui
 * stanno anche i pezzi comuni alle altre schede: i segnali per giocatore e le
 * statistiche di squadra.
 */

import { type ReactNode, useState } from 'react';
import type { Formato, Tabellino as DatiTabellino, TipoEvento } from '@calcio/shared';
import { ArrowDownIcon, ArrowUpIcon, ChartColumnIcon, UsersIcon } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { StatoCaricamento } from '../comuni/Testata.js';
import { CardVista } from '../comuni/Riquadri.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';
import { Ordinabile } from '../comuni/Ordinabile.js';
import { type Ordine, ordina, prossimoOrdine } from '../statistiche/statistiche.js';
import {
  type NomeGiocatore,
  type Segnale,
  type TipoSegnale,
  type TotaliSquadra,
  segnaliPerGiocatore,
  nomeBreve,
  totaliSquadra,
} from './partita.js';
import { IconaEvento } from './eventi.js';
import { usePartita } from './contesto.js';

export interface IntestazionePartita {
  id: string;
  squadraStagioneId: string;
  stato: 'programmata' | 'formazione' | 'in_corso' | 'conclusa' | 'annullata';
  avversario: string;
  inCasa: boolean;
  dataOra: string;
  luogo: string | null;
  note: string | null;
  competizione: string;
}

export interface RispostaTabellino {
  partita: IntestazionePartita;
  formato: Formato;
  tabellino: DatiTabellino;
  giocatori: NomeGiocatore[];
}

/** Ogni totale con l'icona dell'evento che conta. */
const VOCI_TOTALI: { campo: keyof TotaliSquadra; etichetta: string; tipo: TipoEvento }[] = [
  { campo: 'gol', etichetta: 'Gol', tipo: 'gol' },
  { campo: 'assist', etichetta: 'Assist', tipo: 'assist' },
  { campo: 'tiriSpecchio', etichetta: 'Tiri in porta', tipo: 'tiro_specchio' },
  { campo: 'tiriFuori', etichetta: 'Tiri fuori', tipo: 'tiro_fuori' },
  { campo: 'passaggiCompletati', etichetta: 'Passaggi riusciti', tipo: 'passaggio_completato' },
  { campo: 'passaggiSbagliati', etichetta: 'Passaggi sbagliati', tipo: 'passaggio_sbagliato' },
  { campo: 'palleConquistate', etichetta: 'Palle recuperate', tipo: 'palla_conquistata' },
  { campo: 'pallePerse', etichetta: 'Palle perse', tipo: 'palla_persa' },
  { campo: 'gialli', etichetta: 'Gialli', tipo: 'cartellino_giallo' },
  { campo: 'rossi', etichetta: 'Rossi', tipo: 'cartellino_rosso' },
  { campo: 'autogol', etichetta: 'Autogol', tipo: 'autogol' },
];

/** Le colonne della formazione dopo i minuti: in testa solo l'icona. I cartellini stanno nel nome. */
const COLONNE_FORMAZIONE = [
  'gol',
  'assist',
  'passaggiCompletati',
  'passaggiSbagliati',
  'palleConquistate',
  'pallePerse',
] as const satisfies (keyof TotaliSquadra)[];
type Colonna = (typeof COLONNE_FORMAZIONE)[number];

const VOCE = new Map(VOCI_TOTALI.map((v) => [v.campo, v]));

const DESCRIZIONE_SEGNALE: Record<TipoSegnale, string> = {
  entrata: 'Entrato al',
  uscita: 'Uscito al',
  giallo: 'Ammonito al',
  rosso: 'Espulso al',
};

/** Un segnale compatto, icona e minuto, da mettere accanto al nome. */
export function SegnaleGiocatore({ segnale }: { segnale: Segnale }) {
  const icona =
    segnale.tipo === 'entrata' ? (
      <ArrowUpIcon aria-hidden className="size-3.5 text-stato-disponibile" />
    ) : segnale.tipo === 'uscita' ? (
      <ArrowDownIcon aria-hidden className="size-3.5 text-destructive" />
    ) : (
      <IconaEvento
        tipo={segnale.tipo === 'giallo' ? 'cartellino_giallo' : 'cartellino_rosso'}
        className="size-3.5"
      />
    );
  const descrizione = `${DESCRIZIONE_SEGNALE[segnale.tipo]} ${segnale.minuto}`;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[13px] font-normal text-muted-foreground tabular-nums"
      title={descrizione}
      aria-label={descrizione}
    >
      {icona}
      {segnale.minuto}
    </span>
  );
}

/** La scheda Tabellino della partita conclusa: i dati li legge la pagina, per la testata. */
export function SchedaTabellino() {
  const partita = usePartita();
  if (!partita?.tabellino) return <StatoCaricamento errore={partita?.errore ?? null} />;
  return (
    <FormazioneEMinuti
      tabellino={partita.tabellino.tabellino}
      giocatori={partita.tabellino.giocatori}
    />
  );
}

function FormazioneEMinuti({
  tabellino,
  giocatori,
}: {
  tabellino: DatiTabellino;
  giocatori: NomeGiocatore[];
}) {
  const per = new Map(giocatori.map((g) => [g.id, g]));
  const [ordine, setOrdine] = useState<Ordine<Colonna | 'minuti'>>({
    colonna: 'giocatore',
    verso: 'asc',
  });
  const segnali = segnaliPerGiocatore(tabellino.voci);

  // In formazione chi è stato in campo, con il nome per ordinare per cognome.
  const presenze = ordina(
    tabellino.statistiche
      .filter((s) => s.titolare || s.minuti > 0)
      .map((s) => {
        const g = per.get(s.giocatoreId);
        return {
          ...s,
          id: s.giocatoreId,
          nome: g?.nome ?? '',
          cognome: g?.cognome ?? '',
          numero: g?.numero ?? null,
        };
      }),
    ordine,
  );
  const intestazione = (
    colonna: Colonna | 'minuti' | 'giocatore',
    contenuto: ReactNode,
    etichetta?: string,
  ) => (
    <Ordinabile
      attiva={ordine.colonna === colonna}
      verso={ordine.verso}
      onClick={() => setOrdine(prossimoOrdine(ordine, colonna))}
      etichetta={etichetta}
    >
      {contenuto}
    </Ordinabile>
  );

  return (
    <CardVista titolo="Formazione e minuti" Icona={UsersIcon} conteggio={presenze.length}>
      {presenze.length === 0 ? (
        <StatoVuoto titolo="Nessun giocatore è sceso in campo" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{intestazione('giocatore', 'Giocatore')}</TableHead>
                <TableHead className="text-right">{intestazione('minuti', 'Minuti')}</TableHead>
                {COLONNE_FORMAZIONE.map((campo) => {
                  const { etichetta, tipo } = VOCE.get(campo)!;
                  return (
                    <TableHead key={campo} className="text-right">
                      {/* Il tooltip sta su uno span: con asChild il suo
                        data-state coprirebbe quello del bottone. */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            {intestazione(
                              campo,
                              <IconaEvento tipo={tipo} className="size-5" />,
                              etichetta,
                            )}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="pointer-events-none">{etichetta}</TooltipContent>
                      </Tooltip>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {presenze.map((s) => (
                <TableRow key={s.giocatoreId}>
                  <TableCell className="font-medium">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      {nomeBreve(per.get(s.giocatoreId))}
                      {segnali.get(s.giocatoreId)?.map((segnale, i) => (
                        <SegnaleGiocatore key={i} segnale={segnale} />
                      ))}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{s.minuti}</TableCell>
                  {COLONNE_FORMAZIONE.map((campo) => (
                    <TableCell key={campo} className="text-right tabular-nums">
                      {s[campo] || ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardVista>
  );
}

/** I totali di squadra, sommati dalle statistiche dei giocatori. */
export function StatisticheSquadra({ tabellino }: { tabellino: DatiTabellino }) {
  const totali = totaliSquadra(tabellino.statistiche);
  return (
    <CardVista titolo="Statistiche di squadra" Icona={ChartColumnIcon}>
      <dl className="grid grid-cols-2 gap-2 @xl:grid-cols-3 @5xl:grid-cols-2 @7xl:grid-cols-3">
        {VOCI_TOTALI.map((v) => (
          <div
            key={v.campo}
            className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card p-2.5"
          >
            <dt className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
              <IconaEvento tipo={v.tipo} className="shrink-0" />
              <span className="truncate">{v.etichetta}</span>
            </dt>
            <dd className="font-semibold tabular-nums">{totali[v.campo]}</dd>
          </div>
        ))}
      </dl>
    </CardVista>
  );
}
